import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import Field from '../components/ui/Field';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const POLICY = [
  { key: 'len', label: 'At least 8 characters long', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'Contains uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Contains lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
  { key: 'num', label: 'Contains number (0-9)', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Contains special character (!@#$%^&*)', test: (p) => /[!@#$%^&*]/.test(p) },
];

function PW({ value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input className="input pr-12" type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted">
        {show ? '🙈' : '👁'}
      </button>
    </div>
  );
}

export default function ChangePassword() {
  const { changePassword } = useAuth();
  const { success, error } = useToast();
  const nav = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const met = POLICY.map((p) => ({ ...p, ok: p.test(next) }));
  const allMet = met.every((m) => m.ok);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!current) return setErr('Enter your current password.');
    if (!allMet) return setErr('New password does not meet the password policy.');
    if (next !== confirm) return setErr('New password and confirmation do not match.');
    setBusy(true);
    try {
      await changePassword({ currentPassword: current, newPassword: next });
      success('Password updated successfully.');
      nav('/dashboard');
    } catch (x) {
      setErr(x.response?.data?.message || 'Could not update password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Secure Your Account" tagline="Security first, always — keep your workspace protected.">
      <h2 className="font-display text-2xl font-semibold text-brand-900">Change Password</h2>
      <p className="mt-1 text-sm text-content-muted">Update your password to keep your account secure.</p>

      {err && <p className="mt-2 rounded-lg bg-danger-50 px-3 py-2 text-sm font-medium text-danger-700">{err}</p>}

      <form onSubmit={submit} className="mt-5 space-y-4">
        <Field label="Current Password" required>
          <PW value={current} onChange={setCurrent} />
        </Field>
        <Field label="New Password" required>
          <PW value={next} onChange={setNext} />
        </Field>
        <Field label="Confirm New Password" required>
          <PW value={confirm} onChange={setConfirm} />
        </Field>

        <div className="rounded-xl border border-line bg-surface-sunken p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-content-muted">Password policy checklist</p>
          <ul className="space-y-1.5">
            {met.map((m) => (
              <li key={m.key} className={`flex items-center gap-2 text-sm ${m.ok ? 'text-success-700' : 'text-content-muted'}`}>
                <span>{m.ok ? '✓' : '○'}</span> {m.label}
              </li>
            ))}
          </ul>
        </div>

        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? 'Updating…' : 'Update Password'}
        </button>
        <Link to="/dashboard" className="block text-center text-sm font-semibold text-brand-700 hover:underline">
          ‹ Back to Dashboard
        </Link>
      </form>
    </AuthLayout>
  );
}